import MarkdownView from 'react-showdown';
import LSTVLink from '~/components/LSTVLink';
import Iframe from './Iframe';
import InstagramPost from './InstagramPost';
import Photo from './Photo';
import TikTokPost from './TikTokPost';
import Video from './Video';

/**
 * Parses a string like `'|param1=First value|param2=Second value'`. Returns undefined if invalid format.
 */
const getTagParams = (str: string) => {
  if (str.length === 0) {
    return {};
  }
  const re = /\|([a-zA-Z0-9_-]+)=([^|]+)/g;
  const result: Record<string, string> = {};
  let match;
  /**
   * Starting position of the search, used to check that we did not skip characters.
   */
  let index = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = re.exec(str))) {
    if (match.index !== index) {
      return;
    }
    const [, name, value] = match;
    index = re.lastIndex;
    result[name] = value;
    if (index === str.length) {
      return result;
    }
  }
  // Return undefined if there are unmatched characters at the end of the string.
};

type Node = { isMd: true; content: string } | { isMd: false; tagType: string; tagParams: Record<string, string> };

const parseTags = (str: string): Node[] => {
  const re = /\{\{([a-zA-Z0-9_-]+)(.*?)\}\}/g;
  let match;
  let index = 0;
  const result: Node[] = [];
  // eslint-disable-next-line no-cond-assign
  while ((match = re.exec(str))) {
    result.push({ isMd: true, content: str.slice(index, match.index) });
    const [content, tagType, serializedParams] = match;
    const tagParams = getTagParams(serializedParams);
    if (tagParams) {
      result.push({ isMd: false, tagType, tagParams });
    } else {
      result.push({ isMd: true, content });
    }
    index = re.lastIndex;
  }
  result.push({ isMd: true, content: str.slice(index) });
  return result;
};

const components: any = {
  a({ href, children, ...props }) {
    return (
      <LSTVLink to={href} {...props}>
        {children}
      </LSTVLink>
    );
  },
};

const Markdown = ({ content }: { content: string }) => (
  <>
    {parseTags(content).map((node, index) =>
      node.isMd === true ? (
        <MarkdownView
          key={index}
          markdown={node.content}
          components={components}
          options={{
            tables: true,
            simplifiedAutoLink: true,
            strikethrough: true,
            tasklists: true,
          }}
        />
      ) : node.tagType === 'photo' ? (
        <Photo key={index} {...(node.tagParams as any)} />
      ) : node.tagType === 'ig_post' ? (
        <InstagramPost key={index} {...(node.tagParams as any)} />
      ) : node.tagType === 'tt_post' ? (
        <TikTokPost key={index} {...(node.tagParams as any)} />
      ) : node.tagType === 'iframe' ? (
        <Iframe key={index} {...(node.tagParams as any)} />
      ) : node.tagType === 'video' ? (
        <Video style={{ margin: '24px 0' }} key={index} {...(node.tagParams as any)} />
      ) : null
    )}
  </>
);

export default Markdown;
